import { z } from "zod"
import { MarketName } from "@/constants/marketName"
import { ModelAddressSchema } from "@/features/subscription"
import { getPaginatedNodesSchema } from "@/schemas/getPaginatedNodesSchema"

const BaseSupplyPointSchema = z.object({
    id: z.string(),
    meterPoint: z.object({
        // Not fetched by every quote request query (e.g. the email quote page).
        id: z.string().optional(),
        address: ModelAddressSchema,
    }),
})

const ElectricitySupplyPointSchema = BaseSupplyPointSchema.extend({
    marketName: z.literal(MarketName.FRA_ELECTRICITY),
})

const GasSupplyPointSchema = BaseSupplyPointSchema.extend({
    marketName: z.literal(MarketName.FRA_GAS),
})

const BaseQuotedProductSchema = z.object({
    monthlyAmount: z.number(),
    product: z.object({
        code: z.string(),
    }),
})

const ElecQuotedProductSchema = BaseQuotedProductSchema.extend({
    id: z.number(),
    marketParams: z.object({
        provider_calendar_id: z.string(),
    }),
    product: BaseQuotedProductSchema.shape.product.extend({
        tags: getPaginatedNodesSchema(z.object({ code: z.string() })),
    }),
})

const ElectricityQuotedSupplyPointSchema = z.object({
    quotedProducts: getPaginatedNodesSchema(ElecQuotedProductSchema),
    supplyPoint: ElectricitySupplyPointSchema,
})

const GasQuotedSupplyPointSchema = z.object({
    quotedProducts: getPaginatedNodesSchema(BaseQuotedProductSchema),
    supplyPoint: GasSupplyPointSchema,
})

const QuotedSupplyPointSchema = z.union([
    ElectricityQuotedSupplyPointSchema,
    GasQuotedSupplyPointSchema,
])

function isElectricitySupplyPoint(
    quotedSupplyPoint: z.infer<typeof QuotedSupplyPointSchema>
): quotedSupplyPoint is z.infer<typeof ElectricityQuotedSupplyPointSchema> {
    return quotedSupplyPoint.supplyPoint.marketName === MarketName.FRA_ELECTRICITY
}

function isGasSupplyPoint(
    quotedSupplyPoint: z.infer<typeof QuotedSupplyPointSchema>
): quotedSupplyPoint is z.infer<typeof GasQuotedSupplyPointSchema> {
    return quotedSupplyPoint.supplyPoint.marketName === MarketName.FRA_GAS
}

export const QuoteRequestSchema = z
    .object({
        quotedSupplyPoints: getPaginatedNodesSchema(QuotedSupplyPointSchema),
    })
    .transform(({ quotedSupplyPoints }) => {
        const electricityQuotedSupplyPoint =
            quotedSupplyPoints.find(isElectricitySupplyPoint) ?? null
        const gasQuotedSupplyPoint = quotedSupplyPoints.find(isGasSupplyPoint) ?? null

        return {
            electricityQuotedSupplyPoint,
            gasQuotedSupplyPoint,
        }
    })
